// Minimal client for calling Firebase HTTPS functions from the browser

// Get Firebase Functions base URL from environment or construct from project ID
const getFunctionsBaseURL = (): string => {
  // Option 1: Explicit environment variable (highest priority)
  if (import.meta.env.VITE_FUNCTIONS_BASE_URL) {
    return import.meta.env.VITE_FUNCTIONS_BASE_URL;
  }

  // Option 2: Construct from Firebase project ID
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'sandbox-fenero-ai';
  const functionsURL = `https://us-central1-${projectId}.cloudfunctions.net`;

  // Option 3: Use /api prefix if on same domain (production)
  if (typeof window !== 'undefined' && window.location.hostname === 'extract.fenero.ai') {
    return '/api';
  }

  return functionsURL;
};

const BASE_URL = getFunctionsBaseURL();

/**
 * Construct webhook URL for progress updates.
 * Uses the same logic as the backend to ensure consistency.
 *
 * @param endpoint - The webhook endpoint name (default: 'iv_webhook_progress')
 */
function getWebhookURL(endpoint: string = 'iv_webhook_progress'): string {
  // Check if using Firebase emulator
  const useEmulator = import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true';
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'extract-fenero-ai';

  if (useEmulator) {
    // For emulator, use localhost
    return `http://localhost:5501/${projectId}/us-central1/${endpoint}`;
  } else {
    // For production, use direct function URL
    const firebaseFunctionsUrl = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL ||
      `https://us-central1-${projectId}.cloudfunctions.net`;
    return `${firebaseFunctionsUrl}/${endpoint}`;
  }
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // result like: data:application/pdf;base64,<content>
      const base64 = result.split(',')[1] || '';
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function getAuthHeader(): Promise<Record<string, string>> {
  try {
    // Lazy import to avoid bundling issues if auth isn't initialized yet
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;

    if (user) {
      const token = await user.getIdToken();
      return { Authorization: `Bearer ${token}` };
    } else {
      console.warn('FunctionsClient: No authenticated user found');
      // For development, add a fallback user ID
      if (import.meta.env.DEV) {
        console.log('FunctionsClient: Using dev fallback user ID');
        return { 'X-User-ID': 'dev-user-123' };
      }
    }
  } catch (error) {
    console.error('FunctionsClient: Auth error:', error);
  }
  return {};
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const authHeader = await getAuthHeader();

  // Check if we have authentication
  if (!authHeader.Authorization && !authHeader['X-User-ID']) {
    console.error('No authentication available for request to:', path);
    throw new Error('Authentication required. Please sign in.');
  }

  try {
    const url = `${BASE_URL.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...authHeader
      },
      body: JSON.stringify(body),
      mode: 'cors',
    });

    if (!res.ok) {
      let errorMessage = `HTTP ${res.status}`;
      const statusCode = res.status;

      // Handle specific auth errors
      if (res.status === 401) {
        errorMessage = 'Authentication failed. Please sign in again.';
      } else if (res.status === 403) {
        errorMessage = 'Access denied. You do not have permission to access this resource.';
      } else if (res.status === 409) {
        // 409 Conflict - preserve status code in message for better error handling
        errorMessage = `HTTP 409 Conflict`;
        try {
          const errorData = await res.json();
          const backendMessage = errorData.error || errorData.message;
          if (backendMessage) {
            errorMessage = `HTTP 409 Conflict: ${backendMessage}`;
          }
        } catch {
          // Keep default 409 message
        }
      } else {
        try {
          const errorData = await res.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          try {
            errorMessage = await res.text();
          } catch {
            // Use default error message
          }
        }
      }

      console.error(`Request to ${path} failed:`, errorMessage);
      // Attach status code to error for better handling
      const error = new Error(errorMessage);
      (error as any).statusCode = statusCode;
      throw error;
    }

    return res.json() as Promise<T>;
  } catch (error) {
    console.error(`Failed to call ${path}:`, error);
    throw error;
  }
}

async function getJson<T>(path: string, params?: Record<string, string>): Promise<T> {
  const authHeader = await getAuthHeader();

  // Check if we have authentication
  if (!authHeader.Authorization && !authHeader['X-User-ID']) {
    console.error('No authentication available for request to:', path);
    throw new Error('Authentication required. Please sign in.');
  }

  try {
    let url = `${BASE_URL.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        ...authHeader
      },
      mode: 'cors',
    });

    if (!res.ok) {
      let errorMessage = `HTTP ${res.status}`;

      if (res.status === 401) {
        errorMessage = 'Authentication failed. Please sign in again.';
      } else if (res.status === 403) {
        errorMessage = 'Access denied. You do not have permission to access this resource.';
      } else {
        try {
          const errorData = await res.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          try {
            errorMessage = await res.text();
          } catch {
            // Use default error message
          }
        }
      }

      console.error(`Request to ${path} failed:`, errorMessage);
      throw new Error(errorMessage);
    }

    return res.json() as Promise<T>;
  } catch (error) {
    console.error(`Failed to call ${path}:`, error);
    throw error;
  }
}

export const functionsClient = {
  async uploadFile(file: File, caseId?: string) {
    // Get current user for authorization
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User must be authenticated to upload documents');
    }

    console.log('Uploading file as user:', user.uid, caseId ? `for case: ${caseId}` : '');
    const content = await fileToBase64(file);

    const requestBody: {
      filename: string;
      content: string;
      content_type: string;
      uid: string;
      case_id?: string;
    } = {
      filename: file.name,
      content,
      content_type: file.type || 'application/octet-stream',
      uid: user.uid  // Include uid in the request
    };

    // Include case_id if provided
    if (caseId) {
      requestBody.case_id = caseId;
    }

    return postJson<{
      success: boolean;
      workId: string;
      documentId: string;
      filename: string;
      storage_path: string;
      download_url: string;
      message: string;
    }>('de_upload', requestBody);
  },

  async getAnnotations(jobId: string) {
    // Ensure user is authenticated
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User must be authenticated to access annotations');
    }

    return postJson<{
      success: boolean;
      jobId: string;
      annotations: any[];
      extractedData: any;
      annotationCount: number;
    }>('de_get_annotations', {
      jobId,
      userId: user.uid  // Include userId for backend validation
    });
  },

  // Note: Document processing is now handled entirely by the Cloud Function
  // Real-time updates come through RTDB via workService
  // No polling or separate processing steps needed

  // Validation now handled through work items

  // Status updates now come through real-time database subscriptions

  async deleteDocument(documentId: string) {
    // Get current user for authorization
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User must be authenticated to delete documents');
    }

    // Get token without forcing refresh (only force on retry after 401)
    let token = await user.getIdToken(); // Don't force refresh by default
    console.log('deleteDocument: Deleting as user:', user.uid);

    // Make authenticated request
    let authHeader = { Authorization: `Bearer ${token}` };

    try {
      const res = await fetch(`${BASE_URL.replace(/\/$/, '')}/delete_document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...authHeader
        },
        body: JSON.stringify({
          documentId,
          userId: user.uid
        }),
        mode: 'cors',
      });

      if (!res.ok) {
        let errorMessage = `HTTP ${res.status}`;
        try {
          const errorData = await res.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          try {
            errorMessage = await res.text();
          } catch {
            // Use default error message
          }
        }
        throw new Error(errorMessage);
      }

      return res.json() as Promise<{
        success: boolean;
        message: string;
        documentId: string;
        workItemsDeleted: number;
      }>;
    } catch (error) {
      console.error('Failed to delete document:', error);
      throw error;
    }
  },

  async getDocument(documentId: string) {
    // Get current user for authorization
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User must be authenticated to access documents');
    }

    // Get token without forcing refresh (only force on retry after 401)
    let token = await user.getIdToken(); // Don't force refresh by default
    console.log('getDocument: Authenticated as:', user.uid);

    // Make authenticated request with both token and userId
    let authHeader = { Authorization: `Bearer ${token}` };

    try {
      let res = await fetch(`${BASE_URL.replace(/\/$/, '')}/de_get_document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...authHeader
        },
        body: JSON.stringify({
          documentId,
          userId: user.uid  // Also send userId in body for backend validation
        }),
        mode: 'cors',
      });

      // If we get 401, try once with a fresh token
      if (res.status === 401) {
        console.log('getDocument: Got 401, refreshing token and retrying...');
        token = await user.getIdToken(true); // Force refresh on 401
        authHeader = { Authorization: `Bearer ${token}` };

        res = await fetch(`${BASE_URL.replace(/\/$/, '')}/de_get_document`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...authHeader
          },
          body: JSON.stringify({
            documentId,
            userId: user.uid
          }),
          mode: 'cors',
        });
      }

      if (!res.ok) {
        let errorMessage = `HTTP ${res.status}`;
        try {
          const errorData = await res.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          try {
            errorMessage = await res.text();
          } catch {
            // Use default error message
          }
        }
        throw new Error(errorMessage);
      }

      return res.json() as Promise<{
        success: boolean;
        url: string;
        storage_path: string;
        filename: string;
        content_type: string;
        size: number;
        metadata: Record<string, any>;
        expires_in: number;
      }>;
    } catch (error) {
      console.error('Failed to get document:', error);
      throw error;
    }
  },

  async submitCase(borrowerInfo: {
    // Borrower Information
    full_name: string;
    email: string;
    phone: string;
    credit_score_estimate?: '740' | '670' | '580' | '300';

    // Employment Information
    employment_status: 'employed' | 'self_employed' | 'unemployed' | 'retired';
    employer_name?: string;
    income_type: 'Unknown' | 'W-2' | 'Self-Employed' | 'Rental' | 'Multiple Sources';
    annual_income: number;
    monthly_debts: number;

    // Loan Details
    loan_type: string;
    loan_purpose: 'purchase' | 'refinance';
    estimated_loan_amount: number;
    down_payment_amount?: number;

    // Property Information
    location_city: string;
    location_state: string;
    zip_code?: string;

    // Co-Borrower
    applying_jointly?: boolean;
    co_borrower_name?: string;
    co_borrower_income?: number;

    // Additional
    notes?: string;

    // Property Image
    property_image_base64?: string;
    property_image_filename?: string;
    property_image_mimetype?: string;
  }) {
    // Get current user for authorization
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User must be authenticated to create cases');
    }

    console.log('Creating case via Firebase function:', borrowerInfo.full_name);

    // Call Firebase function which creates case in both Firestore and Memgraph (via Studio API)
    return postJson<{
      success: boolean;
      case_id: string;
      borrower_name: string;
      status: string;
    }>('case_create', {
      borrower_info: borrowerInfo
    });
  },

  /**
   * Signal that documents are ready and workflow should continue.
   * This is called after the user has uploaded all required documents.
   */
  async signalDocumentsReady(caseId: string) {
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User must be authenticated to signal documents ready');
    }

    console.log('Signaling documents ready for case:', caseId);

    return postJson<{
      success: boolean;
      case_id: string;
      message: string;
    }>('iv_signal_documents_ready', {
      case_id: caseId,
    });
  },

  /**
   * Start income verification workflow for a case.
   * Analyzes documents to calculate qualifying monthly income.
   */
  async startIncomeVerificationWorkflow(caseId: string) {
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User must be authenticated to start workflows');
    }

    const webhookUrl = getWebhookURL();
    console.log('Starting income verification workflow for case:', caseId, 'webhook:', webhookUrl);

    return postJson<{
      success: boolean;
      workflow_id: string;
      case_id: string;
      status: string;
      webhook_url?: string;
    }>('iv_start_workflow', {
      case_id: caseId,
      webhook_url: webhookUrl,
    });
  },

  async submitQuestions(sessionId: string, answers: Record<string, string>) {
    console.log('Submitting clarifying question answers for session:', sessionId);

    return postJson<{
      success: boolean;
      ok: boolean;
      result: {
        qa_pairs: Record<string, any>;
        categories: string[];
      };
    }>('iv_submit_questions', {
      session_id: sessionId,
      answers: answers
    });
  },

  async getCaseStatus(caseId: string) {
    // Get current user for authorization
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User must be authenticated to get case status');
    }

    console.log('Fetching case status for:', caseId);

    // Use GET request with query parameter
    const authHeader = await getAuthHeader();
    const url = `${BASE_URL.replace(/\/$/, '')}/iv_get_case_status?case_id=${encodeURIComponent(caseId)}`;

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        ...authHeader
      },
      mode: 'cors',
    });

    if (!res.ok) {
      let errorMessage = `HTTP ${res.status}`;
      try {
        const errorData = await res.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch {
        try {
          errorMessage = await res.text();
        } catch {
          // Use default error message
        }
      }
      throw new Error(errorMessage);
    }

    return res.json() as Promise<{
      success: boolean;
      case: any;
      work_items: Array<{
        work_id: string;
        status: string;
        progress: number;
        events: any[];
      }>;
    }>;
  },

  async deleteCase(caseId: string) {
    // Get current user for authorization
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User must be authenticated to delete cases');
    }

    console.log('Deleting case:', caseId, 'as user:', user.uid);

    return postJson<{
      success: boolean;
      case_id: string;
      deletion_results: {
        firestore: {
          deleted: number;
          subcollections: Array<{
            name: string;
            deleted: number;
          }>;
        };
        rtdb: {
          deleted: number;
          paths: string[];
        };
        gcs: {
          deleted: number;
          prefixes: Array<{
            prefix: string;
            deleted: number;
          }>;
        };
      };
    }>('case_delete', {
      case_id: caseId
    });
  },

  // ============================================================================
  // DOCUMENT SCANNING METHODS
  // ============================================================================

  async getLoanProgramOptions() {
    console.log('Fetching loan program options');

    return getJson<{
      loan_programs: Array<{
        value: string;
        label: string;
        description?: string;
        enabled?: boolean;
      }>;
      employment_types: Array<{
        value: string;
        label: string;
        description?: string;
      }>;
    }>('de_loan_program_options');
  },

  async startDocumentScan(
    caseId: string,
    loanProgramType: string,
    employmentType: string = 'w2_employed'
  ) {
    // Get current user for authorization
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User must be authenticated to start document scanning');
    }

    // Get Firebase Storage bucket from config
    // Try environment variable first, then fallback to default
    const bucketName = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ||
                      'extract-fenero-ai.firebasestorage.app';
    
    // Construct GCS prefix - documents are stored at uploads/cases/{caseId}/docs/
    const gcsPrefix = `uploads/cases/${caseId}/docs/`;

    // Construct webhook URL for document scanning progress updates
    // Uses ds_webhook which writes directly to Firestore (not RTDB)
    const webhookUrl = getWebhookURL('ds_webhook');

    console.log('Starting document scan for case:', caseId, 'bucket:', bucketName, 'prefix:', gcsPrefix, 'webhook:', webhookUrl);

    return postJson<{
      success: boolean;
      workflow_id: string;
      case_id: string;
      message: string;
    }>('de_start_scan', {
      case_id: caseId,
      loan_program_type: loanProgramType,
      employment_type: employmentType,
      gcs_bucket: bucketName,
      gcs_prefix: gcsPrefix,
      webhook_url: webhookUrl,
      force_restart: true, // Always force restart to avoid 409 conflicts
    });
  },

  async getScanStatus(workflowId: string) {

    return getJson<{
      workflow_id: string;
      case_id: string;
      status: string;
      is_running: boolean;
      result?: {
        success: boolean;
        case_id: string;
        workflow_id?: string;
        documents_found: number;
        document_types: string[];
        classifications: Array<{
          filename: string;
          document_type: string;
          document_subtype?: string;
          confidence: number;
          tax_year?: number;
          gcs_path?: string;
          summary?: string;
          key_information?: Record<string, unknown>;
          quality_score?: number;
          quality_issues?: string[];
        }>;
        compliance_result?: {
          loan_program: string;
          employment_type: string;
          is_compliant: boolean;
          ready_for_processing: boolean;
          documents_found: string[];
          missing_required: Array<{
            document_type: string;
            description: string;
            required?: boolean;
          }>;
          missing_optional: Array<{
            document_type: string;
            description: string;
          }>;
          suggestions: string[];
          warnings: string[];
          coverage_percentage: number;
        };
        ready_for_processing: boolean;
        approval_status: 'pending' | 'approved' | 'rescan_requested';
        approved_by_user: boolean;
        processing_time_ms: number;
        facts_extracted?: number;
        persons_extracted?: number;
      };
      error?: string;
    }>('de_scan_status', { workflow_id: workflowId });
  },

  async approveClassifications(caseId: string) {
    // Get current user for authorization
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User must be authenticated to approve classifications');
    }

    console.log('Approving classifications for case:', caseId);

    return postJson<{
      success: boolean;
      message: string;
      case_id: string;
      workflow_id?: string;
    }>('de_approve_classifications', {
      case_id: caseId,
    });
  },

  async requestRescan(caseId: string) {
    // Get current user for authorization
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User must be authenticated to request rescan');
    }

    console.log('Requesting rescan for case:', caseId);

    return postJson<{
      success: boolean;
      message: string;
      case_id: string;
      workflow_id?: string;
    }>('de_request_rescan', {
      case_id: caseId,
    });
  },

  async cancelScan(caseId: string, reason?: string) {
    // Get current user for authorization
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User must be authenticated to cancel scanning');
    }

    console.log('Cancelling scan for case:', caseId);

    return postJson<{
      success: boolean;
      message: string;
      case_id: string;
      workflow_id?: string;
    }>('de_cancel_scan', {
      case_id: caseId,
      reason: reason || 'Cancelled by user',
    });
  },

  /**
   * Update classifications - DEPRECATED, redirects to saveClassifications.
   * @deprecated Use saveClassifications instead
   */
  async updateClassifications(
    caseId: string,
    updates: Array<{
      filename: string;
      document_type?: string;
      document_subtype?: string;
      tax_year?: number;
      notes?: string;
    }>
  ) {
    console.log('[DEPRECATED] updateClassifications called, redirecting to saveClassifications');
    
    // Convert updates format to full classifications format
    const classifications = updates.map(u => ({
      filename: u.filename,
      document_type: u.document_type || 'unknown',
      document_subtype: u.document_subtype,
      tax_year: u.tax_year,
      confidence: 1.0, // User edits are 100% confidence
    }));

    const result = await this.saveClassifications(caseId, classifications);
    
    // Return in old format for backward compatibility
    return {
      success: result.success,
      message: result.message,
      case_id: result.case_id,
      updated_count: result.memgraph_updated,
    };
  },

  /**
   * Save classifications directly via REST API.
   * 
   * This saves classifications to:
   * - Memgraph (document type, tax year, confidence)
   * - GCS (JSON file for persistence)
   * - MLflow (feedback logging if original_classifications provided)
   * 
   * Users can call this anytime - no workflow waiting needed.
   */
  async saveClassifications(
    caseId: string,
    classifications: Array<{
      filename: string;
      document_type: string;
      document_subtype?: string;
      tax_year?: number;
      confidence?: number;
      gcs_path?: string;
    }>,
    originalClassifications?: Array<{
      filename: string;
      document_type: string;
      confidence?: number;
    }>
  ) {
    // Get current user for authorization
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User must be authenticated to save classifications');
    }

    console.log('Saving classifications for case:', caseId, 'count:', classifications.length);

    return postJson<{
      success: boolean;
      case_id: string;
      memgraph_updated: number;
      gcs_path: string | null;
      feedback_logged: number;
      message: string;
    }>('de_save_classifications', {
      case_id: caseId,
      classifications,
      original_classifications: originalClassifications,
    });
  },

  /**
   * Get available document types from the backend.
   * This is the single source of truth for document types.
   */
  async getDocumentTypes() {
    console.log('Fetching document types from backend');

    return getJson<{
      document_classes: Record<string, string>;
    }>('de_get_document_types');
  },
};

export type FunctionsClient = typeof functionsClient;


